import React from "react";
import ColorPicker from "@/components/ColorPicker";
import Slider from "@/components/Slider";
import { Toggle } from "@/components/Toggle";
import { StyleConfigModel } from "@/pages/Messaging/types";
import LogoUpload from "../../LogoUpload";

interface Props {
  workingStyleConfig: Partial<StyleConfigModel>;
  updateStyleConfig: (updates: Partial<StyleConfigModel>) => void;
  setStagedLauncherIconFile?: (file: File | null) => void;
}

type IconStyle = NonNullable<StyleConfigModel["launcherIconStyle"]>;
type LauncherShape = NonNullable<StyleConfigModel["launcherShape"]>;

const ICON_STYLES: { value: IconStyle; label: string; svg: React.ReactNode }[] = [
  {
    value: "default",
    label: "Default",
    svg: (
      <path
        d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 4v-4H6a2 2 0 0 1-2-2V6Z"
        fill="currentColor"
      />
    )
  },
  {
    value: "chat-bubble",
    label: "Bubble",
    svg: (
      <path
        d="M12 3a9 9 0 0 0-7.6 13.8L3 21l4.4-1.4A9 9 0 1 0 12 3Z"
        fill="currentColor"
      />
    )
  },
  {
    value: "message",
    label: "Message",
    svg: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor" />
        <path d="m4 7 8 6 8-6" stroke="#fff" strokeWidth="1.6" fill="none" />
      </>
    )
  },
  {
    value: "headset",
    label: "Headset",
    svg: (
      <path
        d="M12 3a8 8 0 0 0-8 8v6a2 2 0 0 0 2 2h2v-7H6a6 6 0 1 1 12 0h-2v7h2a2 2 0 0 0 2-2v-6a8 8 0 0 0-8-8Z"
        fill="currentColor"
      />
    )
  },
  {
    value: "sparkle",
    label: "Sparkle",
    svg: (
      <path
        d="M12 2 13.8 9.2 21 11l-7.2 1.8L12 20l-1.8-7.2L3 11l7.2-1.8L12 2Z"
        fill="currentColor"
      />
    )
  },
  {
    value: "envelope",
    label: "Envelope",
    svg: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor" />
        <path d="m3 7 9 7 9-7" stroke="#fff" strokeWidth="1.6" fill="none" />
      </>
    )
  }
];

const SHAPES: { value: LauncherShape; label: string; radius: string }[] = [
  { value: "circle", label: "Circle", radius: "50%" },
  { value: "rounded-square", label: "Rounded", radius: "12px" },
  { value: "squircle", label: "Squircle", radius: "30%" }
];

function darken(hex: string, pct: number) {
  const m = /^#?([a-f0-9]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * (1 - pct)));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - pct)));
  const b = Math.max(0, Math.round((n & 0xff) * (1 - pct)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export function LauncherCustomization({
  workingStyleConfig,
  updateStyleConfig,
  setStagedLauncherIconFile
}: Props) {
  const iconStyle: IconStyle = workingStyleConfig.launcherIconStyle ?? "default";
  const iconColor = workingStyleConfig.launcherIconColor ?? "#FFFFFF";
  const bgColor =
    workingStyleConfig.launcherBackgroundColor ??
    workingStyleConfig.launcherBgColor ??
    workingStyleConfig.actionColor ??
    "#1670E7";
  const shape: LauncherShape = workingStyleConfig.launcherShape ?? "circle";
  const badgeText = workingStyleConfig.launcherBadgeText ?? "";
  const badgeColor = workingStyleConfig.launcherBadgeColor ?? "#EF4444";
  const gradient = workingStyleConfig.launcherGradient ?? false;
  const iconSize = workingStyleConfig.launcherIconSize ?? 24;

  const radius =
    SHAPES.find((s) => s.value === shape)?.radius ?? "50%";

  const background = gradient
    ? `linear-gradient(135deg, ${bgColor} 0%, ${darken(bgColor, 0.1)} 100%)`
    : bgColor;

  const selectedSvg =
    ICON_STYLES.find((i) => i.value === iconStyle)?.svg ?? ICON_STYLES[0].svg;

  return (
    <div className="space-y-4 pt-2">
      <div className="text-sm font-medium">Launcher Customization</div>

      {/* Mini live preview */}
      <div className="flex items-center justify-center bg-slate-50 border border-slate-200 rounded-md p-4">
        <div
          aria-label="Launcher mini preview"
          style={{
            width: 60,
            height: 60,
            borderRadius: radius,
            background,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
          }}
        >
          {iconStyle === "custom" && workingStyleConfig.launcherIconUrl ? (
            <img
              src={workingStyleConfig.launcherIconUrl}
              alt=""
              style={{ width: iconSize, height: iconSize, objectFit: "contain" }}
            />
          ) : (
            <svg
              width={iconSize}
              height={iconSize}
              viewBox="0 0 24 24"
              style={{ color: iconColor }}
            >
              {selectedSvg}
            </svg>
          )}
          {badgeText.trim().length > 0 && (
            <span
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                background: badgeColor,
                color: "#fff",
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 999,
                whiteSpace: "nowrap"
              }}
            >
              {badgeText}
            </span>
          )}
        </div>
      </div>

      {/* Icon style picker */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
          Icon Style
        </div>
        <div
          className="grid grid-cols-3 sm:grid-cols-4 gap-2"
          role="radiogroup"
          aria-label="Launcher icon style"
        >
          {ICON_STYLES.map((opt) => {
            const isSelected = iconStyle === opt.value;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={`Launcher icon ${opt.label}`}
                key={opt.value}
                className={`flex flex-col items-center gap-1 cursor-pointer p-2 rounded-lg border-2 min-h-[64px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                  isSelected
                    ? "border-primary bg-primary-bg"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                onClick={() => updateStyleConfig({ launcherIconStyle: opt.value })}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  style={{ color: bgColor }}
                >
                  {opt.svg}
                </svg>
                <span
                  className={`text-[11px] ${
                    isSelected ? "text-primary-active font-medium" : "text-slate-500"
                  }`}
                >
                  {opt.label}
                </span>
              </button>
            );
          })}
          {/* Custom upload tile */}
          <button
            type="button"
            role="radio"
            aria-checked={iconStyle === "custom"}
            aria-label="Launcher icon custom upload"
            className={`flex flex-col items-center gap-1 cursor-pointer p-2 rounded-lg border-2 min-h-[64px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
              iconStyle === "custom"
                ? "border-primary bg-primary-bg"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
            onClick={() => updateStyleConfig({ launcherIconStyle: "custom" })}
          >
            <span className="text-2xl text-slate-400 leading-none">+</span>
            <span
              className={`text-[11px] ${
                iconStyle === "custom"
                  ? "text-primary-active font-medium"
                  : "text-slate-500"
              }`}
            >
              Custom
            </span>
          </button>
        </div>
      </div>

      {iconStyle === "custom" && setStagedLauncherIconFile && (
        <LogoUpload
          setStageLogoFile={setStagedLauncherIconFile}
          update={updateStyleConfig}
          workingStyleConfig={workingStyleConfig as StyleConfigModel}
          field="launcherIconUrl"
          label="Custom Icon"
          previewBackground={bgColor}
          sizeGuidance="PNG or SVG • Square image recommended • Replaces the default chat bubble icon"
        />
      )}

      {/* Shape */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
          Shape
        </div>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Launcher shape">
          {SHAPES.map((opt) => {
            const isSelected = shape === opt.value;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={`Launcher shape ${opt.label}`}
                key={opt.value}
                className={`flex items-center gap-2 rounded-lg py-1.5 px-3 cursor-pointer border-2 text-sm min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                  isSelected
                    ? "border-primary bg-primary-bg text-primary-active"
                    : "border-slate-300 bg-white text-slate-600"
                }`}
                onClick={() => updateStyleConfig({ launcherShape: opt.value })}
              >
                <span
                  className="bg-slate-400"
                  style={{ width: 18, height: 18, borderRadius: opt.radius }}
                />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Colors */}
      <div className="grid grid-cols-3 items-center gap-4">
        <div className="text-sm font-normal">Background</div>
        <ColorPicker
          value={bgColor}
          onChange={(v) => updateStyleConfig({ launcherBackgroundColor: v })}
        />
        <div />
      </div>
      <div className="grid grid-cols-3 items-center gap-4">
        <div className="text-sm font-normal">Icon Color</div>
        <ColorPicker
          value={iconColor}
          onChange={(v) => updateStyleConfig({ launcherIconColor: v })}
        />
        <div />
      </div>

      {/* Gradient */}
      <div className="flex justify-between items-center">
        <div>
          <div className="text-sm font-normal">Subtle gradient</div>
          <div className="text-xs text-slate-400">
            Adds a 10% darker shade to the bottom-right of the launcher
          </div>
        </div>
        <Toggle
          toggled={gradient}
          changeToggle={() => updateStyleConfig({ launcherGradient: !gradient })}
        />
      </div>

      {/* Icon size */}
      <div>
        <div className="flex justify-between items-center">
          <div className="text-sm font-medium mb-1">Icon Size</div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              className="border border-slate-300 rounded-md p-2 w-16 text-sm"
              value={iconSize}
              min={12}
              max={48}
              onChange={(e) => {
                let v = parseInt(e.target.value);
                if (isNaN(v)) v = 24;
                if (v < 12) v = 12;
                if (v > 48) v = 48;
                updateStyleConfig({ launcherIconSize: v });
              }}
            />
            <span className="text-xs text-slate-400">px</span>
          </div>
        </div>
        <Slider
          min={12}
          max={48}
          value={iconSize}
          onChange={(v) => updateStyleConfig({ launcherIconSize: v })}
        />
      </div>

      {/* Badge */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
          Badge (optional)
        </div>
        <div className="grid grid-cols-3 items-center gap-4 mb-2">
          <div className="text-sm font-normal">Text</div>
          <input
            type="text"
            aria-label="Launcher badge text"
            className="border border-slate-300 rounded-md p-2 text-sm col-span-2"
            placeholder='e.g. "Chat" or "Help" — empty = none'
            maxLength={12}
            value={badgeText}
            onChange={(e) => updateStyleConfig({ launcherBadgeText: e.target.value })}
          />
        </div>
        {badgeText.trim().length > 0 && (
          <div className="grid grid-cols-3 items-center gap-4">
            <div className="text-sm font-normal">Color</div>
            <ColorPicker
              value={badgeColor}
              onChange={(v) => updateStyleConfig({ launcherBadgeColor: v })}
            />
            <div />
          </div>
        )}
      </div>
    </div>
  );
}
